/**
 * Server state, and the defaults that decide how a console behaves when the answer is stale.
 *
 * The defaults here are chosen for an **operations console**, which is a different animal from a
 * content site. What is on screen is being acted on: an ops lead reading the dispatch board is
 * about to reassign one of those rows, and a stale row is a wrong decision, not a cosmetic lag.
 * So refetch-on-focus is *on* — coming back to the tab is exactly when the screen must be true.
 *
 * The one rule that is not a preference: **an `ApiError` is not retried unless it plausibly was a
 * blip.** Retrying a 403 asks the server the same forbidden question three times and delays the
 * honest "you do not hold that scope" by a second and a half. Retrying a 422 re-sends invalid
 * input. Only a 5xx or a dead network gets another go.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './client';

/** A minute is long enough to stop a nav bounce refetching, short enough to stay honest. */
const STALE_MS = 60_000;
const MAX_ATTEMPTS = 3;

export function retryable(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  /* status 0 is "the network never answered", which is the one worth trying again. */
  return error.status === 0 || error.status >= 500;
}

export function shouldRetry(failureCount: number, error: unknown): boolean {
  return failureCount < MAX_ATTEMPTS - 1 && retryable(error);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: STALE_MS,
        refetchOnWindowFocus: true,
        retry: shouldRetry,
      },
      mutations: {
        /* **Mutations are never retried automatically.** Several of them move money, and the two
           that do carry an `Idempotency-Key` minted when the confirm dialog opens — a silent retry
           underneath that would either replay (harmless but confusing) or, for anything without a
           key, repeat. A failed mutation is shown to the operator, who decides. */
        retry: false,
      },
    },
  });
}

/**
 * Query keys, namespaced per domain, in one place.
 *
 * Scattered string arrays are how a mutation ends up invalidating almost the right thing. Every
 * key is built here so an invalidation and the query it targets cannot drift apart.
 */
export const keys = {
  me: () => ['me'] as const,
  /* ⛔ NOT `['me', 'notifications']`. TanStack matches by PREFIX, so nesting it under `me()`
     would mean every profile save also refetched the notification matrix — the two are separate
     reads of one screen, and a first draft of this key said they were independent while spelling
     them as parent and child. */
  meNotifications: () => ['me-notifications'] as const,
  meRecoveryCodes: () => ['me-recovery-codes'] as const,
  ownSessions: () => ['own-sessions'] as const,

  admins: {
    all: () => ['admins'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['admins', 'list', params] as const,
    presets: () => ['admins', 'presets'] as const,
  },

  /* No `all()` here, unlike its neighbours: those exist so a mutation can invalidate a whole
     family, and nothing mutates a job. The panel polls. A key with no caller is the same shape
     as an endpoint with no caller — it reads as a capability that does not exist. */
  jobs: {
    health: () => ['jobs', 'health'] as const,
    runs: (name: string) => ['jobs', 'runs', name] as const,
    errors: () => ['jobs', 'errors'] as const,
  },
  /* One key: the flush answers with the new version, and invalidating it re-reads the count. */
  /* Read-only: nothing in the console writes what this reports, so nothing invalidates it. */
  ops: { status: () => ['ops', 'status'] as const, drills: () => ['ops', 'drills'] as const },
  responseCache: {
    state: () => ['response-cache', 'state'] as const,
  },
  audit: {
    all: () => ['audit'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['audit', 'list', params] as const,
    event: (id: string) => ['audit', 'event', id] as const,
    entity: (type: string, id: string) => ['audit', 'entity', type, id] as const,
    digests: () => ['audit', 'digests'] as const,
  },

  /* No `all()` here: coverage is read-only, so nothing invalidates the namespace and a key
     nobody calls is a key that goes stale without anybody noticing. */
  supply: {
    coverage: () => ['supply', 'coverage'] as const,
    liquidity: () => ['supply', 'liquidity'] as const,
  },

  pandits: {
    all: () => ['pandits'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['pandits', 'list', params] as const,
    one: (id: number) => ['pandits', 'one', id] as const,
    skills: (id: number) => ['pandits', 'skills', id] as const,
    quality: (id: number) => ['pandits', 'quality', id] as const,
    jobs: (id: number) => ['pandits', 'jobs', id] as const,
    availability: (id: number) => ['pandits', 'availability', id] as const,
  },

  catalogue: {
    all: () => ['catalogue'] as const,
    pujas: (params: Readonly<Record<string, unknown>>) => ['catalogue', 'pujas', params] as const,
    puja: (id: number) => ['catalogue', 'puja', id] as const,
    cityAvailability: (pujaId: number) => ['catalogue', 'city-availability', pujaId] as const,
    categories: () => ['catalogue', 'categories'] as const,
    axis: (axis: string) => ['catalogue', 'axis', axis] as const,
  },

  availability: {
    all: () => ['availability'] as const,
    conflicts: (params: Readonly<Record<string, unknown>>) =>
      ['availability', 'conflicts', params] as const,
    blackouts: (params: Readonly<Record<string, unknown>>) =>
      ['availability', 'blackouts', params] as const,
  },

  bookings: {
    all: () => ['bookings'] as const,
    board: (params: Readonly<Record<string, unknown>>) => ['bookings', 'board', params] as const,
    byState: () => ['bookings', 'by-state'] as const,
    one: (id: number) => ['bookings', 'one', id] as const,
    timeline: (id: number) => ['bookings', 'timeline', id] as const,
    moves: (id: number) => ['bookings', 'moves', id] as const,
    /* Keyed on the public code, not the id: the evidence endpoints are addressed by `BMP…`
       because that is what appears in a support conversation, and a second key derived from
       the numeric id would be a second cache entry for the same rows. */
    evidenceGrants: (code: string) => ['bookings', 'evidence-grants', code] as const,
    /* What a policy cancellation would refund now (CSR-169). Keyed on the code like the grants. */
    cancellationQuote: (code: string) => ['bookings', 'cancellation-quote', code] as const,
  },

  dispatch: {
    all: () => ['dispatch'] as const,
    history: (bookingId: number) => ['dispatch', 'history', bookingId] as const,
    health: (windowDays: number) => ['dispatch', 'health', windowDays] as const,
    reassignments: (params: Readonly<Record<string, unknown>>) =>
      ['dispatch', 'reassignments', params] as const,
    /* The exclusion diagnostic (E52). Under `dispatch` on purpose: sending a wave changes who is
       already-offered and who is at their cap, so a fresh wave must invalidate this too. */
    candidates: (bookingId: number) => ['dispatch', 'candidates', bookingId] as const,
    explain: (bookingId: number, panditId: number) =>
      ['dispatch', 'explain', bookingId, panditId] as const,
  },

  payables: {
    all: () => ['payables'] as const,
    board: (params: Readonly<Record<string, unknown>>) => ['payables', 'board', params] as const,
    one: (id: number) => ['payables', 'one', id] as const,
  },

  payouts: {
    /* No key for the preview: it is a mutation, deliberately uncached. A cached snapshot is a
       batch created from figures nobody looked at. */
    all: () => ['payouts'] as const,
    batches: (params: Readonly<Record<string, unknown>>) => ['payouts', 'batches', params] as const,
    batch: (id: number) => ['payouts', 'batch', id] as const,
    bankAccounts: (panditProfileId: number) => ['payouts', 'bank-accounts', panditProfileId] as const,
  },

  risk: {
    all: () => ['risk'] as const,
    cases: (includeDecided: boolean, page: number) => ['risk', 'cases', includeDecided, page] as const,
    rules: () => ['risk', 'rules'] as const,
    chargebacks: (includeClosed: boolean, page: number) =>
      ['risk', 'chargebacks', includeClosed, page] as const,
    chargeback: (code: string) => ['risk', 'chargeback', code] as const,
    lists: (listKey: string, includeRemoved: boolean) => ['risk', 'lists', listKey, includeRemoved] as const,
  },

  ritualSchools: {
    all: () => ['ritual-schools'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['ritual-schools', params] as const,
  },
  samagri: {
    all: () => ['samagri'] as const,
    items: () => ['samagri', 'items'] as const,
    kits: () => ['samagri', 'kits'] as const,
    kit: (id: number) => ['samagri', 'kit', id] as const,
  },
  enumLabels: {
    all: () => ['enum-labels'] as const,
    of: (name: string) => ['enum-labels', name] as const,
    /* Under `all`, so an edit on the Enum labels screen refreshes every chip with it. */
    resolved: () => ['enum-labels', 'resolved'] as const,
  },
  templeRemittances: {
    all: () => ['temple-remittances'] as const,
    month: (period: string) => ['temple-remittances', period] as const,
  },

  support: {
    all: () => ['support'] as const,
    tickets: (params: Readonly<Record<string, unknown>>) => ['support', 'tickets', params] as const,
    ticket: (code: string) => ['support', 'ticket', code] as const,
    sla: () => ['support', 'sla'] as const,
    macros: () => ['support', 'macros'] as const,
    agents: () => ['support', 'agents'] as const,
    slaReport: () => ['support', 'sla-report'] as const,
  },

  disputes: {
    all: () => ['disputes'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['disputes', 'list', params] as const,
    one: (code: string) => ['disputes', 'one', code] as const,
    bundle: (code: string) => ['disputes', 'bundle', code] as const,
    matrix: () => ['disputes', 'matrix'] as const,
  },

  comms: {
    all: () => ['comms'] as const,
    booking: (code: string) => ['comms', 'booking', code] as const,
    pool: () => ['comms', 'pool'] as const,
  },

  analytics: {
    all: () => ['analytics'] as const,
    funnel: (params: Readonly<Record<string, unknown>>) => ['analytics', 'funnel', params] as const,
    metrics: (params: Readonly<Record<string, unknown>>) =>
      ['analytics', 'metrics', params] as const,
    eventHealth: () => ['analytics', 'event-health'] as const,
  },

  flags: {
    all: () => ['flags'] as const,
    list: () => ['flags', 'list'] as const,
    report: () => ['flags', 'report'] as const,
    revisions: (key: string) => ['flags', 'revisions', key] as const,
    testers: () => ['flags', 'testers'] as const,
  },

  gatewayApprovals: {
    all: () => ['gatewayApprovals'] as const,
    list: () => ['gatewayApprovals', 'list'] as const,
  },

  cms: {
    all: () => ['cms'] as const,
    pages: (params: Readonly<Record<string, unknown>>) => ['cms', 'pages', params] as const,
    page: (id: number) => ['cms', 'page', id] as const,
    /* The redirect map is a CMS key because a redirect is the tail of a page's life — archiving
       writes most of these rows — so a page mutation invalidating `['cms']` refreshes it too. */
    redirects: () => ['cms', 'redirects'] as const,
  },

  seo: {
    all: () => ['seo'] as const,
    registry: (params: Readonly<Record<string, unknown>>) => ['seo', 'registry', params] as const,
    budget: () => ['seo', 'budget'] as const,
    templates: (params: Readonly<Record<string, unknown>>) => ['seo', 'templates', params] as const,
    meta: (cmsPageId: number) => ['seo', 'meta', cmsPageId] as const,
  },

  reviews: {
    all: () => ['reviews'] as const,
    queue: (params: Readonly<Record<string, unknown>>) => ['reviews', 'queue', params] as const,
    dimensions: () => ['reviews', 'dimensions'] as const,
    responses: () => ['reviews', 'responses'] as const,
    qualityFlags: () => ['reviews', 'quality-flags'] as const,
    divergence: (days: number) => ['reviews', 'profile-divergence', days] as const,
    summary: () => ['reviews', 'summary'] as const,
  },

  notificationHelp: () => ['notification-help'] as const,
  lintRules: () => ['content-lint', 'rules'] as const,
  deityEntity: (id: number) => ['deity-entity', id] as const,

  cancellationPolicies: {
    all: () => ['cancellation-policies'] as const,
    one: (id: number) => ['cancellation-policies', id] as const,
  },

  refunds: {
    all: () => ['refunds'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['refunds', 'list', params] as const,
    adjustments: (status: string) => ['refunds', 'adjustments', status] as const,
  },

  ledger: {
    /* No `all()`: nothing invalidates the whole namespace. The ledger is read-only, and the one
       write — rebuilding a snapshot — invalidates that month and nothing else. A key nobody
       calls is a key nobody can be wrong about. */
    journal: (params: Readonly<Record<string, unknown>>) =>
      ['ledger', 'journal', params] as const,
    one: (transactionId: number) => ['ledger', 'one', transactionId] as const,
    booking: (bookingId: number) => ['ledger', 'booking', bookingId] as const,
    trialBalance: (month: string) => ['ledger', 'trial-balance', month] as const,
    moneySummary: (month: string) => ['ledger', 'money-summary', month] as const,
    accounts: () => ['ledger', 'accounts'] as const,
    statement: (pandit: number, fy: string) => ['ledger', 'statement', pandit, fy] as const,
  },

  webhooks: {
    all: () => ['webhooks'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['webhooks', 'list', params] as const,
    one: (eventId: number) => ['webhooks', 'one', eventId] as const,
  },

  payments: {
    // No `all()`: this console is read-only, so nothing here ever needs bulk invalidation.
    // An unused key is one somebody later invalidates believing it does something.
    list: (params: Readonly<Record<string, unknown>>) => ['payments', 'list', params] as const,
    one: (orderId: number) => ['payments', 'one', orderId] as const,
    health: (hours: number) => ['payments', 'health', hours] as const,
  },

  notifications: {
    all: () => ['notifications'] as const,
    catalogue: () => ['notifications', 'catalogue'] as const,
    templates: (params: Readonly<Record<string, unknown>>) =>
      ['notifications', 'templates', params] as const,
    quietHours: () => ['notifications', 'quiet-hours'] as const,
    gateways: () => ['notifications', 'gateways'] as const,
    deliverability: (days: number) => ['notifications', 'deliverability', days] as const,
    suppressions: (channel: string, page: number) =>
      ['notifications', 'suppressions', channel, page] as const,
    messages: (params: Readonly<Record<string, unknown>>) =>
      ['notifications', 'messages', params] as const,
    campaigns: (page: number) => ['notifications', 'campaigns', page] as const,
    campaignAudience: (id: number) => ['notifications', 'campaigns', id, 'audience'] as const,
    campaignProgress: (id: number) => ['notifications', 'campaigns', id, 'progress'] as const,
  },

  media: {
    // `all()` because a replace or a delete changes the grid, both objects' usage panels and
    // (for a delete) the preview at once — invalidating each by hand is how one gets missed.
    all: () => ['media'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['media', 'list', params] as const,
    usages: (id: number) => ['media', 'usages', id] as const,
    preview: (id: number, variant: string) => ['media', 'preview', id, variant] as const,
  },

  temples: {
    all: () => ['temples'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['temples', 'list', params] as const,
    one: (id: number) => ['temples', 'one', id] as const,
    authorizations: (templeId: number) => ['temples', 'authorizations', templeId] as const,
    expiring: () => ['temples', 'expiring'] as const,
  },

  pricing: {
    all: () => ['pricing'] as const,
    packages: (pujaId: number) => ['pricing', 'packages', pujaId] as const,
    versions: (packageId: number) => ['pricing', 'versions', packageId] as const,
    version: (versionId: number) => ['pricing', 'version', versionId] as const,
    preview: (versionId: number, cityId: number | undefined) =>
      ['pricing', 'preview', versionId, cityId ?? null] as const,
    quote: (versionId: number, cityId: number, drop: readonly string[]) =>
      ['pricing', 'quote', versionId, cityId, [...drop].sort().join(',')] as const,
    feeRules: () => ['pricing', 'fee-rules'] as const,
    travelRules: (zoneId: number) => ['pricing', 'travel-rules', zoneId] as const,
    travelSample: (params: Readonly<Record<string, unknown>>) => ['pricing', 'travel-sample', params] as const,
  },

  geo: {
    all: () => ['geo'] as const,
    cities: (params: Readonly<Record<string, unknown>>) => ['geo', 'cities', params] as const,
    areas: (cityId: number) => ['geo', 'areas', cityId] as const,
    localities: (cityId: number) => ['geo', 'localities', cityId] as const,
    zones: (cityId: number) => ['geo', 'zones', cityId] as const,
    waitlist: (cityId: number) => ['geo', 'waitlist', cityId] as const,
    states: () => ['geo', 'states'] as const,
    districts: (stateId: number) => ['geo', 'districts', stateId] as const,
  },

  kyc: {
    all: () => ['kyc'] as const,
    queue: (params: Readonly<Record<string, unknown>>) => ['kyc', 'queue', params] as const,
    case: (id: number) => ['kyc', 'case', id] as const,
    history: (panditId: number) => ['kyc', 'history', panditId] as const,
    rejectionTemplates: () => ['kyc', 'rejection-templates'] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['users', 'list', params] as const,
    one: (id: number) => ['users', 'one', id] as const,
    consents: (id: number) => ['users', 'consents', id] as const,
    dsr: (id: number) => ['users', 'dsr', id] as const,
    aggregates: () => ['users', 'aggregates'] as const,
  },

  offers: {
    all: () => ['offers'] as const,
    list: (params: Readonly<Record<string, unknown>>) => ['offers', 'list', params] as const,
    spend: (offerId: number) => ['offers', 'spend', offerId] as const,
    // The paged key extends the unpaged one, so a mint invalidates every page of codes.
    coupons: (offerId: number, page?: number) =>
      page === undefined
        ? (['offers', 'coupons', offerId] as const)
        : (['offers', 'coupons', offerId, page] as const),
    abuse: (days: number) => ['offers', 'abuse', days] as const,
    scopeOptions: () => ['offers', 'scope-options'] as const,
    redemptions: (offerId: number, params: Readonly<Record<string, unknown>>) =>
      ['offers', 'redemptions', offerId, params] as const,
  },
  loyalty: {
    all: () => ['loyalty'] as const,
    referrals: (params: Readonly<Record<string, unknown>>) =>
      ['loyalty', 'referrals', params] as const,
    referral: (id: number) => ['loyalty', 'referral', id] as const,
    statement: (userId: number, page: number) =>
      ['loyalty', 'statement', userId, page] as const,
    liability: () => ['loyalty', 'liability'] as const,
    adjustments: (status: string) => ['loyalty', 'adjustments', status] as const,
  },
  abuse: {
    all: () => ['abuse'] as const,
    blocklist: (params: Readonly<Record<string, unknown>>) =>
      ['abuse', 'blocklist', params] as const,
    /* A principal is keyed by BOTH halves. `user:42` and `ip:42` are different callers and must
       never share a cache entry, for the same reason they never share a rate bucket. */
    principal: (type: string, value: string) => ['abuse', 'principal', type, value] as const,
  },
  search: {
    all: () => ['search'] as const,
    synonyms: (groupKey: string | undefined) => ['search', 'synonyms', groupKey ?? null] as const,
    aliases: (entity: string, entityId: number | undefined) =>
      ['search', 'aliases', entity, entityId ?? null] as const,
    zeroResults: (params: Readonly<Record<string, unknown>>) =>
      ['search', 'zero-results', params] as const,
  },
  recon: {
    all: () => ['recon'] as const,
    day: (day: string) => ['recon', 'day', day] as const,
    breaks: (params: Readonly<Record<string, unknown>>) => ['recon', 'breaks', params] as const,
    aging: () => ['recon', 'aging'] as const,
    close: (period: string) => ['recon', 'close', period] as const,
    fees: () => ['recon', 'fees'] as const,
  },

  tax: {
    all: () => ['tax'] as const,
    rates: () => ['tax', 'rates'] as const,
    tdsRegister: (params: Readonly<Record<string, unknown>>) =>
      ['tax', 'tds-register', params] as const,
    challans: (fy: string) => ['tax', 'challans', fy] as const,
    filings: (fy: string) => ['tax', 'filings', fy] as const,
    gstRegister: (params: Readonly<Record<string, unknown>>) =>
      ['tax', 'gst-register', params] as const,
    approvals: (status: string) => ['tax', 'approvals', status] as const,
    pan: (panditProfileId: number) => ['tax', 'pan', panditProfileId] as const,
  },

  documents: {
    all: () => ['documents'] as const,
    register: (params: Readonly<Record<string, unknown>>) => ['documents', 'register', params] as const,
    gapAudit: (fy: string) => ['documents', 'gap-audit', fy] as const,
  },

  settings: {
    all: () => ['settings'] as const,
    list: (group: string | undefined) => ['settings', 'list', group ?? null] as const,
    one: (key: string) => ['settings', 'one', key] as const,
    revisions: (key: string) => ['settings', 'revisions', key] as const,
    recent: () => ['settings', 'recent'] as const,
    approvals: () => ['settings', 'approvals'] as const,
    deprecations: () => ['settings', 'deprecations'] as const,
  },
} as const;
